import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Award,
    Clock,
    Phone,
    MessageCircle,
    CheckCircle2,
    Pencil,
    Calculator,
    Hammer,
    Factory,
    Scissors,
    Wrench,
    Shield,
    Wind,
    FileCheck,
    Layers,
    RefreshCw,
    Globe,
    Trophy,
    Building2,
    GraduationCap,
    Home as HomeIcon,
    Landmark,
    Download,
    ArrowRight,
    Menu,
    ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { CrossSiteNavMobile, SiteUtilityBar } from "@/components/CrossSiteNav";
import StickyMobileCTABar from "@/components/StickyMobileCTABar";
import BackToTop from "@/components/BackToTop";
import Lightbox from "@/components/Lightbox";
import InlineCTABanner from "@/components/InlineCTABanner";
import HeroQuickForm from "@/components/HeroQuickForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import SkycoverLogo from "@/components/SkycoverLogo";
import VideoSection from "@/components/VideoSection";
import YouTubeShortsRow from "@/components/YouTubeShortsRow";
import videos from "@/data/videos.json";
import { applySEO, orgSchema, localBusinessSchema, breadcrumbSchema, productSchema, faqSchema, videoObjectSchema } from "@/lib/seo";
import { submitLead, submitCatalogLead, trackEvent } from "@/lib/api";

const TEL = "+52 81 8989 9822";
const TEL_RAW = "528189899822";
const WA_RAW = "528116289267";
const WA_HERO =
    "https://wa.me/" +
    WA_RAW +
    "?text=" +
    encodeURIComponent(
        "Hola, quiero cotizar mi proyecto de velaria.",
    );
const WA_FLOATING_MSG =
    "Hola, me interesa cotizar un proyecto de velaria.";

const SKYCOVER_BLUE = "#1a4379";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/f5542c4d8af9706564d5262ae741552cc678eb812e552d75f8132c966f2be3b1.jpeg";

const TYPES = [
    {
        id: "malla-sombra",
        title: "Velaria de Malla Sombra",
        desc: "Velas de tela sombra de alta densidad. Para terrazas, patios y áreas residenciales premium.",
        price: "Desde $1,500/m²",
        img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/deb252d682e0a46f62523bbe80c1c50c753a0e4e9697f1d4fe8ed031651c7873.jpeg",
        cta: "Cotizar malla sombra",
    },
    {
        id: "membrana-pvc",
        title: "Membrana PVC",
        desc: "Tela arquitectónica de PVC de alta resistencia. 100% impermeable. Vida útil 30 años.",
        price: "Desde $3,500/m²",
        img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/be702712bd268f3abe60b49fc91692038124128db95c2d200193c82137371ff3.jpeg",
        cta: "Cotizar PVC",
    },
    {
        id: "tensoestructura",
        title: "Tensoestructura compleja",
        desc: "Geometrías arquitectónicas únicas con cables y mástiles. Para proyectos icónicos.",
        price: "Cotización por proyecto",
        img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/64fbb1c46e71553d6236c509fb6bd38b22f131c58fc32c2bd31431123fe0d673.jpeg",
        cta: "Solicitar propuesta",
    },
];

const NICHES = [
    { Icon: Trophy, title: "Canchas deportivas", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/381fd30c709de832965aa6b7b76c03c3af6feb568b352b535f945b6852bdcf5d.jpeg", tipo: "Cancha deportiva" },
    { Icon: Building2, title: "Plazas y centros comerciales", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/bf5157e22cba8eb3a8e30878ef4d0e9354fd2e8fe2a8a6fe832572567584aa34.jpeg", tipo: "Plaza o mall" },
    { Icon: Factory, title: "Industrial / Logística", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/689154a9e199c8fdd9a966461b3697ce1d0f26221866aeccbf51f7ce4c9722d2.jpeg", tipo: "Industrial" },
    { Icon: GraduationCap, title: "Educativo", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/df0e9def9538a419c82546444b9a8fdf9168a6276a6bcdebc699d5ec4e8b3d6b.jpeg", tipo: "Educativo" },
    { Icon: HomeIcon, title: "Residencial premium", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/f8cd1b410459e0cc0b008ede99bf3697012b80760bd1926172e9c96cd2ad9732.jpeg", tipo: "Residencial premium" },
    { Icon: Landmark, title: "Vial / Cultural", img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/bf5157e22cba8eb3a8e30878ef4d0e9354fd2e8fe2a8a6fe832572567584aa34.jpeg", tipo: "Otro" },
];

const PROCESS_STEPS = [
    { n: "01", Icon: Pencil, title: "Diseño", desc: "Análisis del espacio, anteproyecto, render 3D" },
    { n: "02", Icon: Calculator, title: "Ingeniería", desc: "Cálculo estructural, simulación de túnel de viento" },
    { n: "03", Icon: Hammer, title: "Obra civil", desc: "Cimentaciones, anclajes, preparación del terreno" },
    { n: "04", Icon: Factory, title: "Fabricación", desc: "Estructura metálica en taller propio" },
    { n: "05", Icon: Scissors, title: "Confección", desc: "Corte y termosellado de la membrana" },
    { n: "06", Icon: Wrench, title: "Instalación", desc: "Montaje, tensado y entrega final" },
];

const DIFFS = [
    { Icon: Award, title: "Premio IFAI 2021", desc: "El único reconocimiento internacional del país para tensoestructuras" },
    { Icon: Wind, title: "Simulación de viento", desc: "Análisis CFD para resistencia certificada" },
    { Icon: FileCheck, title: "Memoria de cálculo", desc: "Documentación estructural incluida en cada proyecto" },
    { Icon: Layers, title: "Membranas premium", desc: "Dickson, Sioen, Serge Ferrari, Polyfab, Verseidag" },
    { Icon: Shield, title: "Garantía por escrito", desc: "Hasta 15 años en membrana, 10 en estructura" },
    { Icon: RefreshCw, title: "Mantenimiento 6 meses", desc: "Visita de seguimiento sin costo incluida" },
    { Icon: Globe, title: "Cobertura nacional", desc: "Instalación en toda la República Mexicana" },
];

const GALLERY = [
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/403303f408cf62b819015795a5133181559b5e4844ff766111dba141564ad666.jpeg", title: "Cancha de pádel", city: "Monterrey", filter: "Canchas" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/e2b53bd47c3ce0556b2b65e4e4debf816e6309861b513812a90ef262dcc74839.jpeg", title: "Gonher Industrial", city: "Monterrey", filter: "Industrial" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/c029578d93edd7076dceb123fd2ffb5942015c02eab0e78e5adf938d6536c845.jpeg", title: "Torre Benavides", city: "Monterrey", filter: "Comercial" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/bf5157e22cba8eb3a8e30878ef4d0e9354fd2e8fe2a8a6fe832572567584aa34.jpeg", title: "Plaza Comercial", city: "Saltillo", filter: "Comercial" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/df0e9def9538a419c82546444b9a8fdf9168a6276a6bcdebc699d5ec4e8b3d6b.jpeg", title: "Centro Educativo", city: "Guadalajara", filter: "Educativo" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/f8cd1b410459e0cc0b008ede99bf3697012b80760bd1926172e9c96cd2ad9732.jpeg", title: "Residencia Premium", city: "Monterrey", filter: "Residencial" },
];

const TIPOS_PROYECTO = ["Cancha de pádel", "Cancha deportiva", "Plaza o mall", "Industrial", "Educativo", "Residencial premium", "Otro"];
const M2_OPTIONS = ["Menos de 100 m²", "100-500 m²", "500-1,000 m²", "1,000-5,000 m²", "Más de 5,000 m²"];
const CIUDADES = ["Monterrey", "Saltillo", "Guadalajara", "CDMX", "Mérida", "Cancún", "Otra"];

const FAQS = [
    { q: "¿Cuánto cuesta una velaria por m²?", a: "El precio varía según el material: malla sombra desde $1,500/m², membrana PVC desde $3,500/m². Cotiza gratis para tu proyecto." },
    { q: "¿Qué tan grande puede ser?", a: "Diseñamos desde 100 m² hasta proyectos de más de 5,000 m². Cada proyecto incluye cálculo estructural propio." },
    { q: "¿La ingeniería viene incluida?", a: "Sí. Todos nuestros proyectos incluyen memoria de cálculo, planos estructurales y simulación de túnel de viento sin costo extra." },
    { q: "¿Cuánto tarda el proyecto completo?", a: "Entre 6 y 16 semanas según complejidad y tamaño. Diseño 1-2 semanas, fabricación 3-6 semanas, instalación 1-3 semanas." },
    { q: "¿Resiste lluvia y granizo?", a: "Las membranas PVC son 100% impermeables y resistentes al granizo. La malla sombra tiene drenaje natural con pendiente." },
    { q: "¿Trabajan en todo México?", a: "Sí, cobertura nacional. Tenemos proyectos en Monterrey, CDMX, Guadalajara, Cancún, Mérida y más." },
    { q: "¿Qué membranas usan?", a: "Trabajamos con las marcas premium del mercado: Dickson, Sioen, Serge Ferrari, Polyfab y Verseidag. Vida útil hasta 30 años." },
    { q: "¿Dan garantía por escrito?", a: "Sí. 15 años en membrana PVC, 10 años en estructura, 1 año en instalación. Premio IFAI 2021." },
    { q: "¿Incluyen mantenimiento?", a: "Sí, incluimos visita de mantenimiento a los 6 meses sin costo. Planes anuales opcionales." },
    { q: "¿Hacen factura?", a: "Sí, facturamos a empresas y aceptamos esquemas de pago con anticipo + saldo contra avance." },
];

// ---------- Sub-componentes ----------

function NavbarVel({ onCotizar }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setMobileOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header data-testid="velarias-navbar" className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "bg-black/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}>
            <SiteUtilityBar active="velarias" accent={SKYCOVER_BLUE} testId="velarias-utility-bar" />
            <div className="container-eclipse flex items-center justify-between h-20 gap-4">
                <Link to="/velarias" data-testid="velarias-navbar-logo" className="flex items-center gap-3 leading-none flex-shrink-0">
                    <img src="/brand/eclipse_logo.png" alt="Eclipse Cubiertas" className="h-10 w-auto object-contain" />
                    <div className="hidden sm:flex items-center border-l border-white/20 pl-3">
                        <SkycoverLogo className="h-11 xl:h-12 w-auto" />
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
                    <Link to="/" data-testid="vel-nav-inicio" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">INICIO</Link>

                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="vel-nav-tipos-trigger" className="flex items-center gap-1 text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">
                            TIPOS <ChevronDown className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-black/95 backdrop-blur-md border-white/10 w-56">
                            {TYPES.map((t) => (
                                <DropdownMenuItem key={t.id} data-testid={`vel-nav-tipo-${t.id}`} onClick={() => scrollTo(t.id)} className="cursor-pointer font-body text-white hover:text-eclipse-cta focus:text-eclipse-cta focus:bg-white/5">
                                    <span className="font-display font-bold text-sm">{t.title}</span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <button onClick={() => scrollTo("proceso")} data-testid="vel-nav-proceso" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">PROCESO</button>
                    <button onClick={() => scrollTo("proyectos")} data-testid="vel-nav-proyectos" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">PROYECTOS</button>
                    <button onClick={() => scrollTo("materiales")} data-testid="vel-nav-materiales" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">MATERIALES</button>
                    <button onClick={() => scrollTo("faq")} data-testid="vel-nav-faq" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">FAQ</button>
                    <button onClick={() => scrollTo("cotizar")} data-testid="vel-nav-contacto" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">CONTACTO</button>
                </nav>

                <div className="flex items-center gap-3">
                    <a href={`tel:${TEL_RAW}`} data-testid="velarias-navbar-phone" className="hidden xl:inline-flex items-center gap-2 text-xs font-display font-semibold text-white hover:text-eclipse-cta transition-colors">
                        <Phone className="w-4 h-4" />
                        {TEL}
                    </a>
                    <button onClick={onCotizar} data-testid="velarias-navbar-cta" className="btn-eclipse-cta hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider">Cotizar</button>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild data-testid="vel-nav-mobile-trigger">
                            <button aria-label="Abrir menú" className="lg:hidden p-2 rounded-md text-white">
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-white w-80 p-0 overflow-y-auto">
                            <SheetHeader className="px-6 pt-6 pb-4 border-b border-neutral-200 bg-eclipse-primary flex flex-row items-center">
                                <SheetTitle asChild>
                                    <div className="flex items-center gap-3">
                                        <img src="/brand/eclipse_logo.png" alt="Eclipse" className="h-9 w-auto object-contain" />
                                        <div className="flex items-center border-l border-white/20 pl-3">
                                            <SkycoverLogo className="h-9 w-auto" />
                                        </div>
                                    </div>
                                </SheetTitle>
                                <SheetDescription className="sr-only">Menú Skycover</SheetDescription>
                            </SheetHeader>
                            <div className="px-6 pt-6 pb-2">
                                <CrossSiteNavMobile active="velarias" onNavigate={() => setMobileOpen(false)} accent={SKYCOVER_BLUE} />
                            </div>
                            <nav className="flex flex-col p-6 pt-2 gap-1">
                                <MobileItemVel label="Inicio" onClick={() => { setMobileOpen(false); navigate("/"); }} testId="vel-mobile-nav-inicio" />
                                <MobileItemVel label="Tipos" onClick={() => scrollTo("tipos")} testId="vel-mobile-nav-tipos" />
                                <MobileItemVel label="Proceso" onClick={() => scrollTo("proceso")} testId="vel-mobile-nav-proceso" />
                                <MobileItemVel label="Proyectos" onClick={() => scrollTo("proyectos")} testId="vel-mobile-nav-proyectos" />
                                <MobileItemVel label="Materiales" onClick={() => scrollTo("materiales")} testId="vel-mobile-nav-materiales" />
                                <MobileItemVel label="FAQ" onClick={() => scrollTo("faq")} testId="vel-mobile-nav-faq" />
                                <MobileItemVel label="Contacto" onClick={() => scrollTo("cotizar")} testId="vel-mobile-nav-contacto" />
                                <a href={`tel:${TEL_RAW}`} className="mt-2 inline-flex items-center gap-2 py-3 px-2 font-display font-semibold text-eclipse-primary hover:text-eclipse-cta">
                                    <Phone className="w-4 h-4" /> {TEL}
                                </a>
                                <button onClick={() => { setMobileOpen(false); onCotizar(); }} data-testid="vel-mobile-nav-cta" className="btn-eclipse-cta mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-sm uppercase tracking-wider">
                                    Solicitar propuesta
                                </button>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}

function MobileItemVel({ label, onClick, testId }) {
    return (
        <button onClick={onClick} data-testid={testId} className="w-full text-left py-3 px-2 font-display font-semibold text-base text-eclipse-primary hover:text-eclipse-cta hover:bg-neutral-50 rounded-md transition-colors">
            {label}
        </button>
    );
}

function HeroVel({ onCotizar }) {
    return (
        <section data-testid="velarias-hero" className="relative min-h-[100vh] flex items-end overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
            <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.55) 50%, rgba(10,10,10,0.2) 100%)" }} aria-hidden />
            <div className="relative z-20 container-eclipse pt-32 pb-16 sm:pb-24 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-12 items-end">
                    <div className="max-w-4xl">
                        <p className="font-display font-bold text-xs sm:text-sm tracking-[0.3em] text-eclipse-cta mb-5 fade-up">SKYCOVER · PREMIUM</p>
                        <h1 data-testid="velarias-hero-title" className="font-display font-black text-white text-[36px] leading-[1.05] sm:text-5xl lg:text-[60px] lg:leading-[1.05] tracking-tight mb-6 fade-up" style={{ animationDelay: "0.1s" }}>
                            Velarias y <span className="text-eclipse-cta">Tensoestructuras</span><br />a la Medida
                        </h1>
                        <p className="text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed mb-10 fade-up" style={{ animationDelay: "0.2s" }}>
                            Diseño, ingeniería, fabricación e instalación. Premio IFAI 2021. Cobertura nacional desde 100 m².
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
                            <a href={WA_HERO} target="_blank" rel="noopener noreferrer" data-testid="velarias-hero-whatsapp" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider bg-[#25D366] hover:bg-[#1ebd5b] text-white transition-all">
                                <MessageCircle className="w-5 h-5" />
                                Cotizar por WhatsApp
                            </a>
                            <button onClick={onCotizar} data-testid="velarias-hero-cta" className="btn-eclipse-cta px-7 py-4 rounded-sm text-sm tracking-wider uppercase">Solicitar propuesta técnica →</button>
                        </div>
                    </div>
                    <div className="fade-up" style={{ animationDelay: "0.35s" }}>
                        <HeroQuickForm sitio="velarias" producto="skycover" accentLabel="Propuesta técnica 24h" testId="velarias-hero-quickform" />
                    </div>
                </div>
                <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-white/15 fade-up" style={{ animationDelay: "0.4s" }}>
                    {[{ Icon: Award, t: "Premio IFAI 2021" }, { Icon: Clock, t: "+20 años" }, { Icon: Building2, t: "+100 proyectos premium" }, { Icon: Calculator, t: "Ingeniería incluida" }].map(({ Icon, t }) => (
                        <div key={t} className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-eclipse-cta flex-shrink-0" strokeWidth={1.75} />
                            <span className="text-white/90 text-xs sm:text-sm font-medium">{t}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function StatsBarVel() {
    const stats = [
        { value: "+20", label: "Años de experiencia" },
        { value: "+100", label: "Proyectos premium" },
        { value: "Nacional", label: "Cobertura" },
        { value: "IFAI 2021", label: "Premio internacional" },
    ];
    return (
        <section className="bg-eclipse-primary py-16 sm:py-20" data-testid="velarias-stats">
            <div className="container-eclipse">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
                    {stats.map((s) => (
                        <div key={s.value} className="text-center sm:text-left">
                            <div className="font-display font-black text-eclipse-cta text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-2 leading-none">{s.value}</div>
                            <p className="text-white/75 text-xs sm:text-sm">{s.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ThreeTypes({ onCotizar }) {
    return (
        <section id="tipos" data-testid="velarias-types" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">3 SISTEMAS</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Tres soluciones según tu proyecto</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TYPES.map((t) => (
                        <article key={t.id} id={t.id} data-testid={`velarias-type-${t.id}`} className="bg-white border border-neutral-200 hover:border-eclipse-cta transition-all rounded-sm overflow-hidden hover:shadow-xl flex flex-col">
                            <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                                <img src={t.img} alt={t.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="font-display font-bold text-xl text-eclipse-primary mb-2 leading-tight">{t.title}</h3>
                                <p className="text-sm text-eclipse-muted mb-4 flex-1">{t.desc}</p>
                                <p className="font-display font-black text-eclipse-cta text-sm mb-4">{t.price}</p>
                                <button onClick={onCotizar} data-testid={`velarias-type-${t.id}-cta`} className="btn-eclipse-outline-dark text-xs py-2.5 px-3 rounded-sm">{t.cta}</button>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function NichesVel({ onSelectNiche }) {
    return (
        <section data-testid="velarias-niches" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-12">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">APLICACIONES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Para cualquier proyecto premium</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {NICHES.map(({ Icon, title, img, tipo }) => (
                        <button key={title} onClick={() => onSelectNiche(tipo)} data-testid={`velarias-niche-${title.toLowerCase().replace(/\s|\//g, "-").replace(/[^\w-]/g, "")}`} className="group relative aspect-[4/3] overflow-hidden bg-eclipse-primary rounded-sm text-left">
                            <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                <Icon className="w-6 h-6 mb-2 text-eclipse-cta" strokeWidth={1.75} />
                                <h3 className="font-display font-bold text-lg leading-tight">{title}</h3>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Process() {
    return (
        <section data-testid="velarias-process" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">METODOLOGÍA</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Cómo trabajamos: 6 etapas</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {PROCESS_STEPS.map(({ n, Icon, title, desc }) => (
                        <div key={n} className="bg-eclipse-light p-6 rounded-sm border-l-4 border-eclipse-cta">
                            <div className="flex items-center gap-3 mb-3">
                                <span className="font-display font-black text-3xl text-eclipse-cta leading-none">{n}</span>
                                <Icon className="w-5 h-5 text-eclipse-primary" strokeWidth={1.75} />
                            </div>
                            <h3 className="font-display font-bold text-lg text-eclipse-primary mb-1">{title}</h3>
                            <p className="text-sm text-eclipse-muted leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Differentiators() {
    return (
        <section data-testid="velarias-diffs" className="bg-eclipse-primary py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">DIFERENCIADORES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">Por qué Skycover</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {DIFFS.map(({ Icon, title, desc }) => (
                        <div key={title} className="bg-white/[0.04] border border-white/10 hover:border-eclipse-cta/60 p-6 rounded-sm transition-colors">
                            <div className="w-11 h-11 rounded-sm bg-eclipse-cta/15 flex items-center justify-center mb-4">
                                <Icon className="w-5 h-5 text-eclipse-cta" strokeWidth={1.75} />
                            </div>
                            <h3 className="font-display font-bold text-base text-white mb-1.5">{title}</h3>
                            <p className="text-xs text-white/65 leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function QuoteFormVel({ initialTipo }) {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        nombre: "",
        telefono: "",
        email: "",
        tipo_proyecto: initialTipo || "",
        metros_cuadrados: "",
        ciudad: "",
        tiene_planos: "",
        mensaje: "",
    });

    useEffect(() => {
        if (initialTipo) setForm((f) => ({ ...f, tipo_proyecto: initialTipo }));
    }, [initialTipo]);

    const set = (k) => (v) => setForm({ ...form, [k]: typeof v === "object" && v?.target ? v.target.value : v });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim() || form.nombre.trim().length < 2) return toast.error("Ingresa tu nombre completo");
        if (!form.telefono.trim() || form.telefono.replace(/\D/g, "").length < 10) return toast.error("Ingresa un teléfono válido (10 dígitos)");
        if (!form.tipo_proyecto) return toast.error("Selecciona el tipo de proyecto");
        if (!form.metros_cuadrados) return toast.error("Selecciona los metros cuadrados");
        if (!form.ciudad) return toast.error("Selecciona la ciudad");
        if (!form.tiene_planos) return toast.error("Indica si tienes planos");

        setSubmitting(true);
        try {
            // tipo_proyecto va al campo tipo_espacio del modelo
            await submitLead({
                nombre: form.nombre,
                telefono: form.telefono,
                email: form.email || undefined,
                tipo_espacio: form.tipo_proyecto,
                metros_cuadrados: form.metros_cuadrados,
                ciudad: form.ciudad,
                tiene_planos: form.tiene_planos,
                mensaje: form.mensaje,
                sitio: "velarias",
                producto: "skycover",
                fuente: "cotizacion",
            });
            toast.success("¡Listo! Te contactamos en menos de 24h.");
            navigate("/gracias?sitio=velarias");
        } catch (err) {
            toast.error("Hubo un problema. Intenta de nuevo o escríbenos al WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="cotizar" data-testid="velarias-quote-section" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">PROPUESTA TÉCNICA</p>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight mb-4">Solicita tu propuesta técnica</h2>
                        <p className="text-base sm:text-lg text-eclipse-muted mb-8 leading-relaxed">Diseño, ingeniería estructural y cotización detallada sin costo.</p>
                        <ul className="space-y-3 mb-8">
                            {["Memoria de cálculo incluida", "Simulación de túnel de viento", "Garantía hasta 15 años"].map((b) => (
                                <li key={b} className="flex items-center gap-3 text-eclipse-primary font-medium">
                                    <CheckCircle2 className="w-5 h-5 text-eclipse-cta flex-shrink-0" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <a href={`tel:${TEL_RAW}`} data-testid="velarias-form-phone" className="inline-flex items-center gap-3 font-display font-black text-2xl text-eclipse-primary hover:text-eclipse-cta transition-colors">
                            <Phone className="w-6 h-6 text-eclipse-cta" />
                            {TEL}
                        </a>
                    </div>
                    <form onSubmit={submit} data-testid="velarias-quote-form" className="bg-white p-6 sm:p-8 rounded-sm shadow-xl border border-neutral-100">
                        <div className="space-y-4">
                            <Field label="Nombre completo *"><Input value={form.nombre} onChange={set("nombre")} data-testid="velarias-form-nombre" required /></Field>
                            <Field label="Teléfono / WhatsApp *"><Input type="tel" value={form.telefono} onChange={set("telefono")} placeholder="+52 81 1234 5678" data-testid="velarias-form-telefono" required /></Field>
                            <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} data-testid="velarias-form-email" /></Field>
                            <Field label="Tipo de proyecto *">
                                <Select value={form.tipo_proyecto} onValueChange={set("tipo_proyecto")}>
                                    <SelectTrigger data-testid="velarias-form-tipo"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>{TIPOS_PROYECTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                            <Field label="Metros cuadrados *">
                                <Select value={form.metros_cuadrados} onValueChange={set("metros_cuadrados")}>
                                    <SelectTrigger data-testid="velarias-form-m2"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>{M2_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                            <Field label="Ciudad *">
                                <Select value={form.ciudad} onValueChange={set("ciudad")}>
                                    <SelectTrigger data-testid="velarias-form-ciudad"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>{CIUDADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                            <Field label="¿Tienes planos? *">
                                <RadioGroup value={form.tiene_planos} onValueChange={set("tiene_planos")} className="flex flex-wrap gap-4 pt-1">
                                    {[{ v: "si", l: "Sí, tengo planos" }, { v: "en_proceso", l: "En proceso" }, { v: "no", l: "Aún no" }].map((o) => (
                                        <Label key={o.v} className="flex items-center gap-2 cursor-pointer font-normal text-sm">
                                            <RadioGroupItem value={o.v} data-testid={`velarias-form-planos-${o.v}`} />
                                            {o.l}
                                        </Label>
                                    ))}
                                </RadioGroup>
                            </Field>
                            <Field label="Cuéntanos sobre tu espacio (opcional)"><Textarea value={form.mensaje} onChange={set("mensaje")} placeholder="Uso, requerimientos especiales, fechas..." rows={3} data-testid="velarias-form-mensaje" /></Field>
                            <Button type="submit" disabled={submitting} data-testid="velarias-form-submit" className="w-full btn-eclipse-cta py-6 text-sm uppercase tracking-wider rounded-sm">
                                {submitting ? "Enviando..." : "Solicitar propuesta técnica gratis →"}
                            </Button>
                            <p className="text-[11px] text-eclipse-muted text-center">Al enviar aceptas nuestro aviso de privacidad. Respuesta en menos de 24 horas.</p>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-display font-semibold text-eclipse-primary tracking-wider uppercase mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}

function GalleryVel() {
    const [filter, setFilter] = useState("Todos");
    const filters = ["Todos", "Canchas", "Comercial", "Industrial", "Educativo", "Residencial"];
    const items = filter === "Todos" ? GALLERY : GALLERY.filter((g) => g.filter === filter);
    return (
        <section id="proyectos" data-testid="velarias-gallery" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-10">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">PROYECTOS</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Casos reales</h2>
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                    {filters.map((f) => (
                        <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-xs font-display font-bold tracking-wider transition-colors ${filter === f ? "bg-eclipse-primary text-white" : "bg-white text-eclipse-primary border border-neutral-200 hover:border-eclipse-cta"}`}>
                            {f.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((g) => (
                        <article key={g.title} className="group relative aspect-[4/3] overflow-hidden bg-eclipse-primary rounded-sm">
                            <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                <h3 className="font-display font-bold text-lg leading-tight">{g.title}</h3>
                                <p className="text-xs text-white/80">{g.city}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function MaterialsVel() {
    return (
        <section id="materiales" data-testid="velarias-materials" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-12">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">MATERIALES PREMIUM</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Las marcas líderes del mundo</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <article className="bg-white p-7 rounded-sm">
                        <h3 className="font-display font-bold text-2xl text-eclipse-primary mb-4">Membranas PVC arquitectónicas</h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {["Dickson", "Sioen", "Serge Ferrari", "Polyfab", "Verseidag"].map((b) => (
                                <span key={b} className="px-3 py-1.5 bg-eclipse-primary text-white text-xs font-display font-bold tracking-wider rounded-sm">{b.toUpperCase()}</span>
                            ))}
                        </div>
                        <ul className="space-y-2 text-sm text-eclipse-primary">
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />Vida útil hasta 30 años</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />100% impermeable</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />Antifúngico y autolimpiante</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />Resistente al fuego (clase B)</li>
                        </ul>
                    </article>
                    <article className="bg-white p-7 rounded-sm">
                        <h3 className="font-display font-bold text-2xl text-eclipse-primary mb-4">Malla sombra alta densidad</h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                            <span className="px-3 py-1.5 bg-eclipse-primary text-white text-xs font-display font-bold tracking-wider rounded-sm">POLYFAB</span>
                        </div>
                        <ul className="space-y-2 text-sm text-eclipse-primary">
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />95% bloqueo UV</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />Vida útil 20 años</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />Resistencia 70 km/hr</li>
                            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-eclipse-cta" />24 colores disponibles</li>
                        </ul>
                    </article>
                </div>
            </div>
        </section>
    );
}

function CatalogVel() {
    const [submitting, setSubmitting] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) return toast.error("Completa nombre y email");
        setSubmitting(true);
        try {
            const res = await submitCatalogLead({ nombre: name, email, sitio: "velarias" });
            toast.success("¡Listo! Te enviamos también el catálogo a tu correo.");
            window.open(res.catalog_url || "/catalogo_velarias.pdf", "_blank");
        } catch (err) {
            toast.error("No pudimos generar el catálogo. Intenta de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="catalogo" data-testid="velarias-catalog" className="bg-eclipse-primary py-20 sm:py-24">
            <div className="container-eclipse">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <img src="https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/64fbb1c46e71553d6236c509fb6bd38b22f131c58fc32c2bd31431123fe0d673.jpeg" alt="Portada del catálogo Skycover" className="w-full aspect-[4/3] object-cover rounded-sm shadow-2xl" />
                    <div className="text-white">
                        <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">CATÁLOGO PDF</p>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4">Catálogo Skycover</h2>
                        <p className="text-white/80 text-base sm:text-lg mb-8 leading-relaxed">Sistemas, casos premium y fichas técnicas de membranas.</p>
                        <form onSubmit={onSubmit} className="space-y-3 max-w-md">
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" data-testid="velarias-catalog-nombre" className="bg-white/10 border-white/20 text-white placeholder:text-white/50" required />
                            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Tu email" data-testid="velarias-catalog-email" className="bg-white/10 border-white/20 text-white placeholder:text-white/50" required />
                            <Button type="submit" disabled={submitting} data-testid="velarias-catalog-submit" className="w-full btn-eclipse-cta py-5 text-sm uppercase tracking-wider rounded-sm inline-flex items-center justify-center gap-2">
                                <Download className="w-4 h-4" />
                                {submitting ? "Generando..." : "Descargar PDF gratis"}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FaqVel() {
    return (
        <section id="faq" data-testid="velarias-faq" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse max-w-4xl">
                <div className="mb-12">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">DUDAS COMUNES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Preguntas frecuentes</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`item-${i}`} data-testid={`velarias-faq-${i}`} className="border border-neutral-200 rounded-sm px-5">
                            <AccordionTrigger className="font-display font-bold text-base text-eclipse-primary hover:no-underline hover:text-eclipse-cta text-left">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-sm text-eclipse-muted leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

function FinalCTAVel({ onCotizar }) {
    return (
        <section data-testid="velarias-final-cta" className="bg-eclipse-primary py-20 sm:py-28">
            <div className="container-eclipse text-center">
                <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-5">EMPECEMOS HOY</p>
                <h2 className="font-display font-black text-white text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-5">Empieza tu proyecto premium</h2>
                <p className="text-white/85 text-base sm:text-lg max-w-2xl mx-auto mb-10">Propuesta técnica gratuita en menos de 24 horas</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                    <a href={WA_HERO} target="_blank" rel="noopener noreferrer" data-testid="velarias-final-whatsapp" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider bg-[#25D366] hover:bg-[#1ebd5b] text-white transition-all">
                        <MessageCircle className="w-5 h-5" />
                        Cotizar por WhatsApp
                    </a>
                    <button onClick={onCotizar} data-testid="velarias-final-cta-btn" className="btn-eclipse-cta px-7 py-4 rounded-sm text-sm tracking-wider uppercase">Solicitar propuesta</button>
                </div>
                <a href={`tel:${TEL_RAW}`} className="inline-flex items-center gap-3 font-display font-black text-2xl text-white hover:text-eclipse-cta transition-colors">
                    <Phone className="w-6 h-6 text-eclipse-cta" />
                    {TEL}
                </a>
                <p className="text-white/60 text-sm mt-2">Lun-Vie 9-18h</p>
            </div>
        </section>
    );
}

// ---------- Página principal ----------
export default function Velarias() {
    const [initialTipo, setInitialTipo] = useState("");

    useEffect(() => {
        applySEO({
            path: "/velarias",
            title: "Skycover | Velarias y Tensoestructuras a la Medida en México | Eclipse Cubiertas",
            description: "Diseño, ingeniería e instalación de velarias premium y tensoestructuras. Membrana PVC hasta 30 años. Premio IFAI 2021. Cobertura nacional. Cotización gratis.",
            keywords: "velarias Monterrey, tensoestructuras México, membrana PVC, arquitectura textil, Skycover, ingeniería estructural, velarias premium",
            ogImage: "/og/og-skycover.png",
            ogImageAlt: "Skycover — Velarias y tensoestructuras premium a la medida en México",
            jsonLD: [
                orgSchema,
                localBusinessSchema,
                breadcrumbSchema([{ name: "Inicio", path: "/" }, { name: "Skycover", path: "/velarias" }]),
                productSchema({ name: "Skycover — Tensoestructuras y velarias a la medida", description: "Velarias y tensoestructuras arquitectónicas premium con membrana PVC e ingeniería estructural incluida. Garantía 15 años. Premio IFAI 2021.", image: HERO_IMG, sku: "SKYCOVER-PREMIUM", rating: 4.9, reviewCount: 64 }),
                videoObjectSchema({
                    name: videos.skycover_feature[0].title,
                    description: "Skycover — velarias y tensoestructuras premium a la medida con ingeniería estructural incluida.",
                    videoId: videos.skycover_feature[0].videoId,
                }),
                faqSchema(FAQS),
            ],
        });
        trackEvent({ event_type: "page_view", path: "/velarias", sitio: "velarias" });
    }, []);

    const scrollToCotizar = () => {
        const el = document.getElementById("cotizar");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleNicheCta = (tipo) => {
        setInitialTipo(tipo);
        setTimeout(scrollToCotizar, 100);
    };

    return (
        <div className="theme-skycover min-h-screen bg-white" data-testid="velarias-page">
            <NavbarVel onCotizar={scrollToCotizar} />
            <main>
                <HeroVel onCotizar={scrollToCotizar} />
                <Breadcrumbs items={[{ label: "Skycover" }]} testId="velarias-breadcrumbs" />
                <StatsBarVel />
                <InlineCTABanner heading="¿Tu proyecto requiere ingeniería estructural?" subheading="Skycover. Memoria de cálculo y simulación de viento incluidas." whatsappUrl={WA_HERO} tel={TEL_RAW} onCtaClick={scrollToCotizar} ctaLabel="Solicitar propuesta" variant="light" testId="vel-banner-1" />
                <ThreeTypes onCotizar={scrollToCotizar} />
                <NichesVel onSelectNiche={handleNicheCta} />
                <Process />
                <VideoSection
                    eyebrow="Skycover en acción"
                    title="Velarias y tensoestructuras que ya construimos"
                    subtitle="Proyectos premium con ingeniería estructural incluida"
                    videoId={videos.skycover_feature[0].videoId}
                    videoTitle={videos.skycover_feature[0].title}
                    isShort={videos.skycover_feature[0].isShort}
                    bullets={[
                        "Diseño + ingeniería + fabricación + instalación",
                        "Membrana PVC importada, vida útil hasta 30 años",
                        "Simulación de túnel de viento incluida",
                        "Premio IFAI 2021 — único en México",
                    ]}
                    primaryCtaLabel="Solicitar propuesta técnica"
                    onPrimaryCta={scrollToCotizar}
                    sectionId="skycover-video"
                    testId="vel-video-section"
                />
                <YouTubeShortsRow
                    shorts={videos.shorts}
                    title="Mas videos del canal"
                    subtitle="Shorts de velarias y tensoestructuras Skycover"
                    testId="vel-shorts-row"
                />
                <Differentiators />
                <QuoteFormVel initialTipo={initialTipo} />
                <GalleryVel />
                <InlineCTABanner heading="¿Necesitas más información técnica?" subheading="Catálogo PDF, membranas y casos premium." whatsappUrl={WA_HERO} tel={TEL_RAW} onCtaClick={scrollToCotizar} ctaLabel="Cotizar" variant="dark" testId="vel-banner-2" />
                <MaterialsVel />
                <CatalogVel />
                <FaqVel />
                <p className="text-center text-sm text-eclipse-muted py-8 px-4">¿Buscas toldos modulares prefabricados? Visita <a href="/flexshade" className="text-[#FF6801] font-display font-bold underline">FlexShade</a> · Sitio madre <a href="/" className="text-eclipse-primary font-display font-bold underline">Eclipse Cubiertas</a></p>
                <FinalCTAVel onCotizar={scrollToCotizar} />
            </main>
            <FloatingWhatsApp phone={WA_RAW} message={WA_FLOATING_MSG} testId="velarias-floating-wa" />
            <StickyMobileCTABar whatsappUrl={WA_HERO} ctaHref={`tel:${TEL_RAW}`} ctaLabel="Llamar" testId="vel-sticky-cta" />
            <BackToTop testId="vel-back-to-top" />
        </div>
    );
}
